import { RefObject, useMemo } from 'react';
import { useIsomorphicLayoutEffect } from './use-isomorphic-layout-effect';

/**
 * Добавление события на элемент
 * @param {RefObject<HTMLElement[] | HTMLElement>}  refs
 * @param {(target: Element) => void} intersectionObserverCallback
 * @param {IntersectionObserverInit} [options]
 * @param {number}                   lastRefIndex
 */
export const useIntersectionObserver = (
  refs: RefObject<HTMLElement[] | HTMLElement>,
  intersectionObserverCallback: IntersectionObserverCallback,
  options?: IntersectionObserverInit,
  lastRefIndex = 0
) => {
  const intersectionOptions = useMemo(() => options || { threshold: 0.5 }, [options]);

  const intersectionObserver: IntersectionObserver | null = useMemo(() => {
    if (__IS_SERVER__) {
      return null;
    }

    return new IntersectionObserver(intersectionObserverCallback, intersectionOptions);
  }, [intersectionObserverCallback, intersectionOptions]);

  useIsomorphicLayoutEffect(() => {
    if (Array.isArray(refs.current)) {
      const nodeList = refs.current?.slice(lastRefIndex);
      nodeList?.forEach((element: HTMLElement) => {
        intersectionObserver?.observe(element);
      });
    } else if (refs.current){
      intersectionObserver?.observe(refs.current);
    }

    return () => {
      intersectionObserver?.disconnect();
    };
  }, [refs, intersectionObserver, lastRefIndex]);
};
